import { Alert, Button, Card, CardBody, Input } from '@foundation/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './login.module.css'
import { api } from '@/lib/api'

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
})

function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setError(null)
    setApiError(null)

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (!token) {
      setApiError('No reset token provided. Please use the link from your email.')
      return
    }

    setIsLoading(true)
    api
      .resetPassword({ token, password })
      .then(() => {
        setSuccess(true)
      })
      .catch((err: unknown) => {
        setApiError(err instanceof Error ? err.message : 'Password reset failed')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (success) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <CardBody>
            <div className={styles.logo}>
              <span className={styles.logoIcon}>&#10024;</span>
              <span className={styles.logoText}>ProofPulse</span>
            </div>
            <h2 className={styles.title}>Password Reset</h2>
            <Alert variant="success" className={styles.alert}>
              Your password has been reset successfully!
            </Alert>
            <Link to="/login">
              <Button variant="primary" className={styles.fullWidth}>
                Go to Login
              </Button>
            </Link>
          </CardBody>
        </Card>
      </main>
    )
  }

  return (
    <main className={styles.container}>
      <Card className={styles.card}>
        <CardBody>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>&#10024;</span>
            <span className={styles.logoText}>ProofPulse</span>
          </div>
          <h2 className={styles.title}>Set New Password</h2>
          <p className={styles.subtitle}>Enter your new password below.</p>

          {apiError && (
            <Alert variant="danger" className={styles.alert}>
              {apiError}
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className={styles.alert}>
              {error}
            </Alert>
          )}

          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />

            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              className={styles.fullWidth}
            >
              Reset Password
            </Button>
          </form>

          <p className={styles.footer}>
            Remember your password? <Link to="/login">Sign in</Link>
          </p>
        </CardBody>
      </Card>
    </main>
  )
}
